import type { EntityPagerFilter } from "@/graphql/generated-types";
import type { IPagerSupport, WithId } from "@/interfaces";
import { sentryException } from "@/sentry";
import type { PageableQuery } from "@/types";

/**
 * How many items would be fetched in the first chunk?
 */
const FIRST_CHUNK_COUNT = import.meta.env.DEV ? 5 : 100;

/**
 * How many items would be fetched each time, after the first chunk?
 */
const EACH_CHUNK_COUNT = import.meta.env.DEV ? 500 : 2000;

/**
 * Circuit breaker threshold. I don't like to idea to send requests over and
 * over until the time we don't know. We should have a limit no matter what.
 */
const MAX_REQUESTS = 100;

export default async <TData>(
  query: PageableQuery<TData, EntityPagerFilter>,
  variables: EntityPagerFilter,
  store: IPagerSupport<TData>,
) => {
  // Create a unique token for this pager run. We don't wanna have two parallel
  // pager running because they can update the store at the same time.
  const runId = Symbol("pagerRun");
  store.pager.runId = runId;

  // Abort if a new pager run has started
  if (store.pager.runId !== runId) {
    return;
  }

  store.pager.init();

  // Setting the loading status for the first chunk
  store.pager.status = "firstChunk";

  // Fetching the first chunk. We need to use pageInfo to create a while loop.
  const { pageInfo, edges } = await query({
    ...variables,
    first: FIRST_CHUNK_COUNT,
  });

  // Abort if a new pager run has started
  if (store.pager.runId !== runId) {
    return;
  }

  // We should set the store data
  store.pager.items = [...(edges as (TData & WithId)[])];

  // Calling the subscription if there is any to select an item. But, the
  // selected item might not be fetched yet because of pagination. So we should
  // not force to select to first item. That's why we use FALSE parameter here.
  store.pager.callListeners(false);

  // Set the initial values for pagination
  let hasNextPage = pageInfo.hasNextPage;
  let cursor: string | null = pageInfo.endCursor;
  let requestCount = 0;

  // Setting the status for other chunks
  if (hasNextPage) {
    store.pager.status = "otherChunks";
  }

  // Fething other chunks until there is no next page
  while (hasNextPage) {
    // We shouldn't send requests until to end of time.
    if (requestCount >= MAX_REQUESTS) {
      sentryException(new Error("Max pager request limit reached."));
      break;
    }

    // Fetching the next chunk
    const result = await query({
      ...variables,
      first: EACH_CHUNK_COUNT,
      after: cursor,
    });

    // Abort if a new pager run has started
    if (store.pager.runId !== runId) {
      return;
    }

    // Merge items
    store.pager.items.push(...result.edges);

    // Let's try to select the item.
    store.pager.callListeners(false);

    // Setting the next page values
    hasNextPage = result.pageInfo.hasNextPage;
    cursor = result.pageInfo.endCursor;
    requestCount++;
  }

  // Abort if a new pager run has started
  if (store.pager.runId !== runId) {
    return;
  }

  // Set the pagination is done
  store.pager.status = "done";

  // Call the subscriptions to select the correct item. If the selected item is
  // not on the list, the first item should be selected. That's why we use
  // TRUE as a param here.
  store.pager.callListeners(true);
};
